import { approveProduct, deleteProduct, getBrandByIdApi, overrideAdblock, saveProductSuggestion } from "api";
import { useDataStore } from "store/DataStore";
import { MAX_IDENTICAL_SPLIT_ENTRIES, checkDupeEntry, disableSize, maxIdenticalEntries, validateProduct, parseProduct } from "utils";

export const createProductSlice = (set, get) => ({
  products: [],
  addProduct: () => {
    const product = {
      ext_brand_i: null,
      brand_nm: "",
      size_dimension_i: null,
      size_1: "",
      size_2: "",
      size_3: "",
      size_4: "",
      ext_size_unit_i: null,
      ext_size_unit_2_i: null,
      variant_ids: [],
      variant_nms: [],
      origin_ids: [],
      origin_nms: [],
      model_number_ids: [],
      model_number_nms: [],
      liner_f: false,
      index: get().products.length,
      isValid: false,
      sizes: [],
      is_adult_bev: false,
    }
    set((state) => ({
      products: [...state.products, product], currentProduct: product
    }))
  },
  addProducts: (products) => set((state) => ({
    products: [...state.products, ...products],
    currentProduct: products[0] || state.currentProduct
  })),
  removeProduct: (product) => set((state) => {
    const filteredProducts = state.products
      .filter(p => p.index !== product.index)
      .map((p, idx) => ({ ...p, index: idx }));
    return {
      products: filteredProducts,
      currentProduct: filteredProducts[0] || null
    };
  }),
  removeProductByID: async (productId) => {
    const res = await deleteProduct({
      adblockId: get().currentAdBlockID,
      productId: productId
    });
    if (res.success) {
      set((state) => {
        const newProducts = state.products
          .filter(product => product.id !== productId)
          .map((product, idx) => ({ ...product, index: idx }));
        let newCurrentProduct = state.currentProduct;
        if (state.currentProduct && state.currentProduct.id === productId) {
          newCurrentProduct = newProducts.length > 0 ? newProducts[0] : null;
        }
        return {
          products: newProducts,
          currentProduct: newCurrentProduct
        };
      });
      get().updateActiveBlockData({ products_approved: false });
      console.log(get().products);
    } else {
      console.error("Failed to delete product:", res.error);
    }
  },
  updateProduct: (productId, updatedData) => set((state) => ({
    products: state.products.map(product =>
      product.id === productId ? { ...product, ...updatedData } : product
    ),
  })),
  cloneProduct: (productId) => {
    const product = get().products.find(p => p.id === productId);
    if (product) {
      const clonedProduct = {
        ...product,
        id: null,
        uuid: null,
        index: get().products.length,
        open_ai: false,
        isSuggestion: false,
        existingProduct: false,
        addison_agent: false,
      };
      set((state) => ({
        products: [...state.products, clonedProduct],
        currentProduct: clonedProduct
      }));
    }
  },
  cloneProductByIndex: (productIndex) => {
    const product = get().products.find(p => p.index === productIndex);
    if (product) {
      const clonedProduct = {
        ...product,
        id: null,
        uuid: null,
        index: get().products.length,
        open_ai: false,
        isSuggestion: false,
        existingProduct: false,
        addison_agent: false,
      };
      set((state) => ({
        products: [...state.products, clonedProduct],
        currentProduct: clonedProduct
      }));
    }
  },
  currentProduct: null,
  setCurrentProduct: (product) => { set({ currentProduct: product }) },
  updateCurrentProduct: (updatedData, source) => {
    updatedData.isValid = validateProduct({ ...get().currentProduct, ...updatedData });
    updatedData.open_ai = false;
    updatedData.existingProduct = false;
    updatedData.addison_agent = false;
    updatedData.isSuggestion = false;
    set((state) => ({
      currentProduct: source === 'from_suggestion' ? { ...state.currentProduct, ...updatedData, variant_ids: [], origin_ids: [], model_number_ids: [] } : { ...state.currentProduct, ...updatedData },
      products: state.products.map(product =>
        product.index === state.currentProduct.index ? { ...product, ...updatedData } : product
      ),
    }))
  },
  addBrandToCurrentProduct: async ({ brand_uuid = '' }) => {
    const res = await getBrandByIdApi(brand_uuid);
    if (!res.success) {
      return;
    }
    get().updateCurrentProduct({
      brand: res?.results || null,
      brand_nm: res?.results?.brand_name || "",
      brand_name: res?.results?.brand_name || "",
      ext_brand_i: res?.results?.ext_id || null,
      sub_cat_i: res?.results?.sub_cat_i || null,
      sizes: res?.results?.sizes.map(size => ({
        id: size.ext_id,
        title: size.size_c,
        max: size.max,
        min: size.min
      })) || [],
      variant_ids: [],
      variant_nms: [],
      origin_ids: [],
      origin_nms: [],
      model_number_ids: [],
      model_number_nms: [],
      is_adult_bev: res?.results?.is_adult_bev || false,
    });
  },
  initProducts: () => set({ products: [], currentProduct: null }),
  approveProduct: async ({
    override = false,
    onSuccess = () => { }
  } = {}) => {
    if (override) {
      const overrideRes = await overrideAdblock(get().currentAdBlockID);
      if (!overrideRes.success) {
        return;
      }
    }
    let products = get().products;
    if (!products.length > 0) {
      return {
        success: false,
        error: {
          message: "No products entered."
        }
      };
    }

    const meta = get().getMetaData()
    if (meta.split === 0) {
      const hasDuplicates = checkDupeEntry(products);
      if (hasDuplicates) {
        return {
          error: {
            message: "Duplicate Entry Detected",
            description: "Some products have duplicate entries. This is not a split adblock",
            type: "warning",
          },
          success: false
        };
      }
    } else if (maxIdenticalEntries(products) > MAX_IDENTICAL_SPLIT_ENTRIES) {
      // Split blocks legitimately allow duplicate entries, but a flood of
      // identical rows is accidental duplication (e.g. a held clone hotkey).
      return {
        error: {
          message: "Too Many Identical Entries",
          description: `More than ${MAX_IDENTICAL_SPLIT_ENTRIES} identical product entries detected. This looks like accidental duplication, please remove the extra rows and reapprove.`,
          type: "warning",
        },
        success: false
      };
    }
    //remove invalid product variant that set from suggestions
    products.forEach((product) => {
      if (product?.brand?.variants?.length == 0) {
        product.variant_nms = [];
        product.variant_ids = [];
        product.variants = null
        product.variantsData = [];
      }
    })
    products = products.map((product, index) => disableSize({ product, index }));

    const productStageMetaData = { ...get().getMetaData() };
    delete productStageMetaData.coupon_f;
    delete productStageMetaData.coupon_amnt;

    const data = {
      adblock: productStageMetaData,
      products: products,
      size_data: useDataStore.getState().productSizes,
      type: "edit",
    }

    const endTime = new Date().toISOString().replace(',','')

    if (!get().isImported) {
      data.adblock.ltr_product_started_at = get().productStartTime
      data.adblock.ltr_product_finished_at = endTime
    } else {
      data.adblock.product_import_started_at = get().productStartTime
      data.adblock.product_import_finished_at = endTime
    }
    
    const acceptedSuggestions = products.filter(product => product.isSuggestion);
    const res = await approveProduct(get().currentAdBlockID, data);
    if (res.success) {
      get().updateActiveBlockData({ products_approved: true })
      get().updateAdblockField({ products_approved: true })
      set({ isImported: false });

      // Reset products from API response
      if (res.payload && Array.isArray(res.payload)) {
        const updatedProducts = parseProduct({ productData: res.payload });
        set({ products: updatedProducts, currentProduct: updatedProducts[0] || null });
      }

      const saveSuggestions = await saveProductSuggestion({
        id: get().currentAdBlockID,
        data: {
          existing_suggestion_from: get().existingSuggestionID,
          suggestion_type: get().suggestionType,
          suggestions_shown: get().suggestions,
          suggestion_accepted: acceptedSuggestions,
        }
      });

      if (!saveSuggestions.success) {
        console.log("Failed to save suggestion tracking");
      }
      onSuccess();
    }
  },
  handleImportProduct: async (data) => {
    const pData = data.map((product, index) => {
      delete product.uuid;
      return {
        ...product,
        size_2: product.size_dimension_i > 1 ? product.size_2 : null,
        size_3: product.size_dimension_i > 2 ? product.size_3 : null,
        size_4: product.size_dimension_i > 2 ? product.size_4 : null,
        brand_nm: product.brand?.brand_name,
        isValid: validateProduct(product),
        index,
        variant_ids: product?.variant_ids ? product?.variant_ids : [],
        variantsData: product.brand?.variants
          ? product.brand?.variants.map((item) => ({
            id: item.ext_id,
            title: item.variety_nm,
          }))
          : [],
        origin_ids: product?.origin_ids ? product?.origin_ids : [],
        originsData: product.brand?.origins
          ? product.brand?.origins.map((item) => ({
            id: item.ext_id,
            title: item.origin_nm,
          }))
          : [],
        model_number_ids: product?.model_number_ids ? product?.model_number_ids : [],
        modelNumbersData: product.brand?.model_numbers
          ? product.brand?.model_numbers.map((item) => ({
            id: item.ext_id,
            title: item.model_number
          }))
          : [],
        sizes: product.brand?.sizes ? product.brand.sizes.map((item) => ({
          id: item.ext_id,
          title: item.size_c,
          max: item.max,
          min: item.min
        })) : [],
        is_adult_bev: product.brand?.is_adult_bev
      }
    });
    set({ products: pData, isImported: true });
  },
  existingProducts: [],
  existingProductInfo: null,
});
